// Neo-Brutalism landing page — monolithic iframe block
// 4 brand variants: acid / volt / pop / noir

export type BrutalLandingBrand = 'acid' | 'volt' | 'pop' | 'noir'
export interface BrutalLandingPageData { brand: BrutalLandingBrand }
export const DEFAULT_BRUTAL_LANDING_DATA: BrutalLandingPageData = { brand: 'acid' }

// ── CSS ───────────────────────────────────────────────────────────────────────
const BRUTAL_LANDING_CSS = `
/* ================= БАЗА / 4 ПАЛИТРЫ БРЕНДОВ ================= */
*{margin:0;padding:0;box-sizing:border-box}
:root{
  --fd:'Unbounded',sans-serif;
  --fb:'Manrope',sans-serif;
  --fm:'JetBrains Mono',monospace;
  --bw:3px;
}
body[data-brand="acid"]{
  --bg:#FFF4D6; --paper:#FFFFFF; --ink:#141414; --mut:#6b6353;
  --a1:#FFD400; --a2:#FF4D2E; --a3:#2F6BFF; --a4:#59C135;
  --shadow:#141414; --on-a1:#141414; --on-a2:#FFF4D6; --on-a3:#FFF4D6; --on-a4:#141414;
  --dot:rgba(20,20,20,.16);
}
body[data-brand="volt"]{
  --bg:#EDF1FF; --paper:#FFFFFF; --ink:#101336; --mut:#5a5f85;
  --a1:#2B32FF; --a2:#FF7A1A; --a3:#8FB7FF; --a4:#FFD400;
  --shadow:#101336; --on-a1:#FFFFFF; --on-a2:#101336; --on-a3:#101336; --on-a4:#101336;
  --dot:rgba(16,19,54,.16);
}
body[data-brand="pop"]{
  --bg:#FFE9F4; --paper:#FFF7FB; --ink:#221426; --mut:#7c5f74;
  --a1:#FF5EC4; --a2:#8C6BFF; --a3:#4FE3B0; --a4:#FFD400;
  --shadow:#221426; --on-a1:#221426; --on-a2:#FFF7FB; --on-a3:#221426; --on-a4:#221426;
  --dot:rgba(34,20,38,.16);
}
body[data-brand="noir"]{
  --bg:#111210; --paper:#1B1C18; --ink:#F2F0E4; --mut:#a3a093;
  --a1:#C8FF2E; --a2:#FF5C39; --a3:#F2F0E4; --a4:#FFD400;
  --shadow:#C8FF2E; --on-a1:#111210; --on-a2:#111210; --on-a3:#111210; --on-a4:#111210;
  --dot:rgba(242,240,228,.14);
}
html{scroll-behavior:smooth;scroll-padding-top:96px}
body{
  background:var(--bg); color:var(--ink); font-family:var(--fb);
  background-image:radial-gradient(var(--dot) 1.5px, transparent 1.5px);
  background-size:26px 26px; overflow-x:hidden;
  transition:background-color .45s,color .45s;
}
::selection{background:var(--a1);color:var(--on-a1)}
::-webkit-scrollbar{width:12px}
::-webkit-scrollbar-track{background:var(--bg)}
::-webkit-scrollbar-thumb{background:var(--ink);border:3px solid var(--bg)}
img{display:block;max-width:100%}
a{color:inherit}
h1,h2,h3{font-family:var(--fd);text-transform:uppercase}
.sec{padding:96px 0;scroll-margin-top:96px}
.wrap{max-width:1280px;margin:0 auto;padding:0 24px}

/* шумовая плёнка */
.noise{position:fixed;inset:0;z-index:98;pointer-events:none;opacity:.06;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");}

/* кастомный курсор */
.cursor{position:fixed;left:0;top:0;width:14px;height:14px;background:var(--a1);
  border:2px solid var(--ink);z-index:99;pointer-events:none;transform:translate(-50%,-50%);
  transition:width .25s,height .25s,transform .25s,background .3s;display:none}
.cursor.big{width:44px;height:44px;transform:translate(-50%,-50%) rotate(45deg)}
@media(hover:hover) and (pointer:fine){.cursor{display:block}}

/* ================= ШАПКА ================= */
.hdr{position:sticky;top:0;z-index:50;background:var(--bg);border-bottom:var(--bw) solid var(--ink);transition:background-color .45s}
.hdr__in{max-width:1280px;margin:0 auto;padding:14px 24px;display:flex;align-items:center;gap:24px}
.logo{font-family:var(--fd);font-weight:900;font-size:20px;text-decoration:none;
  background:var(--a1);color:var(--on-a1);border:var(--bw) solid var(--ink);
  padding:6px 12px;box-shadow:4px 4px 0 var(--shadow);transition:transform .15s,box-shadow .15s,background .45s}
.logo:hover{transform:translate(-2px,-2px);box-shadow:6px 6px 0 var(--shadow)}
.logo sup{font-size:10px}
.nav{display:flex;gap:4px;margin-left:auto}
.nav a{font-family:var(--fm);font-size:13px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;
  text-decoration:none;padding:8px 12px;border:var(--bw) solid transparent;transition:.15s}
.nav a:hover{border-color:var(--ink);background:var(--paper);box-shadow:3px 3px 0 var(--shadow)}
.hdr__right{display:flex;align-items:center;gap:14px}
.nav ~ .hdr__right{margin-left:0}

/* переключатель 4 брендов */
.switch{display:flex;gap:8px}
.sw{display:flex;align-items:center;gap:7px;font-family:var(--fm);font-weight:700;font-size:12px;
  padding:8px 10px;border:var(--bw) solid var(--ink);background:var(--paper);color:var(--ink);
  box-shadow:3px 3px 0 var(--shadow);cursor:pointer;transition:.15s}
.sw i{width:13px;height:13px;border:2px solid #141414;display:block}
.sw[data-b="acid"] i{background:#FFD400}
.sw[data-b="volt"] i{background:#2B32FF}
.sw[data-b="pop"] i{background:#FF5EC4}
.sw[data-b="noir"] i{background:#C8FF2E}
.sw:hover{transform:translate(-1px,-1px);box-shadow:4px 4px 0 var(--shadow)}
.sw.is-on{background:var(--ink);color:var(--bg)}
.burger{display:none;width:44px;height:44px;border:var(--bw) solid var(--ink);background:var(--a1);
  box-shadow:4px 4px 0 var(--shadow);cursor:pointer;position:relative;z-index:70}
.burger span{position:absolute;left:10px;right:10px;height:3px;background:var(--on-a1)}
.burger span:nth-child(1){top:15px}.burger span:nth-child(2){bottom:15px}
.burger.open span:nth-child(1){top:19px;transform:rotate(45deg)}
.burger.open span:nth-child(2){bottom:19px;transform:rotate(-45deg)}

/* мобильное меню */
.menu{position:fixed;inset:0;background:var(--bg);z-index:60;transform:translateY(-102%);
  transition:transform .5s cubic-bezier(.7,0,.2,1);padding:110px 24px 40px;overflow:auto}
.menu.open{transform:translateY(0)}
.menu a.big{display:block;font-family:var(--fd);font-weight:800;font-size:clamp(26px,7vw,44px);
  text-transform:uppercase;text-decoration:none;padding:14px 0;border-bottom:var(--bw) dashed var(--ink)}
.menu a.big em{font-style:normal;color:var(--a2)}
.menu__foot{margin-top:28px;font-family:var(--fm);font-size:13px;display:flex;gap:20px;flex-wrap:wrap}

/* ================= ХИРО ================= */
.hero{position:relative;padding:72px 0 88px;overflow:hidden}
.hero__grid{display:grid;grid-template-columns:1.08fr .92fr;gap:56px;align-items:center;position:relative;z-index:2}
.eyebrow{font-family:var(--fm);font-size:13px;font-weight:700;letter-spacing:.08em;margin-bottom:26px;
  display:inline-block;border:var(--bw) solid var(--ink);background:var(--paper);
  padding:8px 14px;box-shadow:4px 4px 0 var(--shadow)}
.eyebrow b{color:var(--a2)}
.hero__title{font-weight:900;font-size:clamp(40px,6.4vw,86px);line-height:.99;letter-spacing:-.01em}
.ht-line{display:block}
.ht-mark{display:inline-block;background:var(--a1);color:var(--on-a1);border:var(--bw) solid var(--ink);
  box-shadow:8px 8px 0 var(--shadow);padding:.02em .14em;transform:rotate(-1.5deg);margin:.06em 0}
.ht-outline{color:transparent;-webkit-text-stroke:3px var(--ink)}
.ht-reg{font-style:normal;font-size:.35em;-webkit-text-stroke:0;color:var(--a2);vertical-align:super}
.hero__text{margin:30px 0 34px;max-width:520px;font-size:17px;line-height:1.65;font-weight:600;color:var(--mut)}
.hero__text b{color:var(--ink)}
.hero__cta{display:flex;gap:18px;flex-wrap:wrap}

.btn{display:inline-flex;align-items:center;gap:10px;font-family:var(--fm);font-weight:700;font-size:14px;
  letter-spacing:.05em;text-transform:uppercase;text-decoration:none;cursor:pointer;
  border:var(--bw) solid var(--ink);background:var(--paper);color:var(--ink);
  padding:16px 26px;box-shadow:6px 6px 0 var(--shadow);transition:transform .15s,box-shadow .15s,background .45s}
.btn:hover{transform:translate(-3px,-3px);box-shadow:9px 9px 0 var(--shadow)}
.btn:active{transform:translate(4px,4px);box-shadow:0 0 0 var(--shadow)}
.btn--a1{background:var(--a1);color:var(--on-a1)}
.btn--inv{background:var(--ink);color:var(--bg)}

.hero__tags{display:flex;gap:10px;flex-wrap:wrap;margin-top:34px}
.hero__tags span{font-family:var(--fm);font-size:12px;font-weight:700;letter-spacing:.08em;
  border:2px solid var(--ink);padding:6px 10px;background:var(--paper)}
.hero__tags span:nth-child(odd){transform:rotate(-2deg)}
.hero__tags span:nth-child(even){transform:rotate(2deg)}

.hero__frame{position:relative;border:var(--bw) solid var(--ink);background:var(--paper);
  padding:12px;box-shadow:14px 14px 0 var(--a2);transform:rotate(1.5deg)}
.hero__frame img{width:100%;aspect-ratio:1/1;object-fit:cover;border:var(--bw) solid var(--ink)}
.hero__frame img.pop{animation:imgIn .55s cubic-bezier(.2,.9,.3,1.15)}
@keyframes imgIn{from{opacity:0;transform:scale(.92) rotate(-2deg)}to{opacity:1;transform:scale(1) rotate(0)}}
.hero__cap{display:flex;justify-content:space-between;gap:12px;font-family:var(--fm);font-size:12px;
  font-weight:700;letter-spacing:.08em;padding-top:12px}
.sticker{position:absolute;font-family:var(--fm);font-weight:700;font-size:13px;letter-spacing:.05em;
  border:var(--bw) solid var(--ink);padding:8px 14px;box-shadow:4px 4px 0 var(--shadow);z-index:3}
.hero__st1{top:-18px;left:-18px;background:var(--a2);color:var(--on-a2);transform:rotate(-8deg)}
.hero__st2{bottom:52px;right:-16px;background:var(--a3);color:var(--on-a3);transform:rotate(6deg)}

.fl{position:absolute;z-index:1;animation:floaty 5s ease-in-out infinite}
.fl svg{display:block}
.fl--1{top:12%;left:44%;animation-delay:.4s}
.fl--2{bottom:14%;left:6%;animation-delay:1.2s}
.fl--3{top:8%;right:6%;animation-delay:2s}
@keyframes floaty{0%,100%{transform:translateY(0) rotate(0)}50%{transform:translateY(-16px) rotate(6deg)}}
.scroll{position:absolute;left:24px;bottom:18px;font-family:var(--fm);font-size:12px;font-weight:700;
  letter-spacing:.12em;text-decoration:none;animation:blink 1.6s steps(2) infinite}
@keyframes blink{50%{opacity:.25}}

/* ================= ТИКЕР ================= */
.ticker{border-block:var(--bw) solid var(--ink);background:var(--a1);color:var(--on-a1);overflow:hidden;
  padding:14px 0;transition:background .45s}
.ticker__track{display:flex;width:max-content;animation:tick 24s linear infinite}
.ticker__track span{font-family:var(--fd);font-weight:700;font-size:15px;letter-spacing:.06em;
  text-transform:uppercase;white-space:nowrap;padding-right:12px}
@keyframes tick{to{transform:translateX(-50%)}}

/* ================= СЕКЦИИ ================= */
.sec__head{display:flex;align-items:flex-end;justify-content:space-between;gap:24px;margin-bottom:52px;flex-wrap:wrap}
.sec__label{font-family:var(--fm);font-size:13px;font-weight:700;letter-spacing:.1em;display:inline-block;
  background:var(--ink);color:var(--bg);padding:6px 12px;margin-bottom:18px}
.sec__head h2{font-weight:900;font-size:clamp(30px,4.6vw,58px);line-height:1.02}
.sec__hint{font-family:var(--fm);font-size:12px;font-weight:700;letter-spacing:.06em;color:var(--mut);max-width:280px;text-align:right}
.mark{background:var(--a1);color:var(--on-a1);border:var(--bw) solid var(--ink);
  box-shadow:5px 5px 0 var(--shadow);padding:0 .18em;display:inline-block;transform:rotate(-1deg)}
.mark--a2{background:var(--a2);color:var(--on-a2)}
.mark--a3{background:var(--a3);color:var(--on-a3)}

/* манифест */
.manifest{font-family:var(--fd);font-weight:400;font-size:clamp(19px,2.6vw,30px);line-height:1.5;
  max-width:1000px;margin-bottom:56px}
.manifest__cards{display:grid;grid-template-columns:repeat(3,1fr);gap:26px}
.mcard{border:var(--bw) solid var(--ink);background:var(--paper);padding:26px;
  box-shadow:7px 7px 0 var(--shadow);transition:transform .18s,box-shadow .18s}
.mcard:hover{transform:translate(-3px,-3px);box-shadow:10px 10px 0 var(--shadow)}
.mcard h3{font-size:18px;margin:14px 0 10px}
.mcard p{font-size:15px;line-height:1.6;color:var(--mut);font-weight:600}
.mcard .ico{width:52px;height:52px;border:var(--bw) solid var(--ink);display:grid;place-items:center;
  font-size:24px;box-shadow:4px 4px 0 var(--shadow)}
.mcard:nth-child(1) .ico{background:var(--a1)}
.mcard:nth-child(2) .ico{background:var(--a2)}
.mcard:nth-child(3) .ico{background:var(--a3)}

/* услуги */
.svc-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:26px}
.svc{--ca:var(--a1);position:relative;border:var(--bw) solid var(--ink);background:var(--paper);
  padding:26px;box-shadow:6px 6px 0 var(--shadow);transition:transform .18s,box-shadow .18s;cursor:pointer}
.svc:hover{transform:translate(-4px,-4px);box-shadow:10px 10px 0 var(--ca)}
.svc:nth-child(1){--ca:var(--a1)}.svc:nth-child(2){--ca:var(--a2)}.svc:nth-child(3){--ca:var(--a3)}
.svc:nth-child(4){--ca:var(--a4)}.svc:nth-child(5){--ca:var(--a2)}.svc:nth-child(6){--ca:var(--a3)}
.svc__top{display:flex;justify-content:space-between;align-items:center;margin-bottom:20px}
.svc__num{font-family:var(--fm);font-weight:700;font-size:13px;border:2px solid var(--ink);
  background:var(--ca);padding:4px 8px;box-shadow:3px 3px 0 var(--shadow)}
.svc__arr{font-size:22px;transition:transform .2s}
.svc:hover .svc__arr{transform:translate(4px,-4px) rotate(45deg)}
.svc h3{font-size:19px;margin-bottom:10px}
.svc p{font-size:15px;line-height:1.6;color:var(--mut);font-weight:600}

/* статистика */
.stats{background:var(--ink);color:var(--bg);border-block:var(--bw) solid var(--ink);transition:background .45s}
.stats__in{max-width:1280px;margin:0 auto;padding:64px 24px;display:grid;grid-template-columns:repeat(4,1fr)}
.stat{padding:0 32px;border-left:3px dashed color-mix(in srgb, var(--bg) 45%, transparent)}
.stat:first-child{border-left:none;padding-left:0}
.stat b{font-family:var(--fd);font-weight:900;font-size:clamp(34px,4.6vw,64px);display:block;line-height:1}
.stat b i{font-style:normal;color:var(--a1)}
body[data-brand="volt"] .stat b i{color:var(--a2)}
.stat span{font-family:var(--fm);font-size:12px;font-weight:700;letter-spacing:.1em;display:block;margin-top:12px;opacity:.75}

/* кейсы */
.cases{display:grid;grid-template-columns:1fr 1fr;gap:32px}
.case{position:relative;border:var(--bw) solid var(--ink);background:var(--paper);
  box-shadow:9px 9px 0 var(--shadow);transition:transform .18s,box-shadow .18s}
.case:hover{transform:translate(-4px,-4px);box-shadow:13px 13px 0 var(--shadow)}
.case__img{position:relative;overflow:hidden;border-bottom:var(--bw) solid var(--ink)}
.case__img img{width:100%;aspect-ratio:4/3;object-fit:cover;transition:transform .6s cubic-bezier(.2,.8,.2,1)}
.case:hover .case__img img{transform:scale(1.07) rotate(.5deg)}
.case__badge{position:absolute;top:14px;left:14px;background:var(--a2);color:var(--on-a2);
  border:var(--bw) solid var(--ink);font-family:var(--fm);font-weight:700;font-size:12px;
  letter-spacing:.06em;padding:7px 12px;box-shadow:4px 4px 0 var(--shadow);
  transform:rotate(-4deg);display:none;z-index:2}
.case.is-active .case__badge{display:inline-block;animation:popIn .35s cubic-bezier(.2,.9,.3,1.4)}
@keyframes popIn{from{transform:rotate(-4deg) scale(.5)}to{transform:rotate(-4deg) scale(1)}}
.case__body{padding:24px}
.case__meta{display:flex;gap:14px;font-family:var(--fm);font-size:12px;font-weight:700;
  letter-spacing:.08em;color:var(--mut);margin-bottom:12px}
.case__meta b{color:var(--ink)}
.case h3{font-size:clamp(18px,2vw,24px);margin-bottom:10px}
.case p{font-size:15px;color:var(--mut);font-weight:600;line-height:1.55}
.case__tags{display:flex;gap:8px;flex-wrap:wrap;margin-top:16px}
.case__tags span{font-family:var(--fm);font-size:11px;font-weight:700;letter-spacing:.06em;
  border:2px solid var(--ink);padding:4px 9px;background:var(--bg)}

/* процесс */
.steps{position:relative;display:grid;grid-template-columns:repeat(4,1fr);gap:26px}
.steps::before{content:"";position:absolute;left:0;right:0;top:36px;border-top:3px dashed var(--ink);opacity:.5}
.step{position:relative;z-index:1}
.step__num{width:72px;height:72px;border:var(--bw) solid var(--ink);display:grid;place-items:center;
  font-family:var(--fd);font-weight:900;font-size:22px;box-shadow:5px 5px 0 var(--shadow);margin-bottom:20px}
.step:nth-child(1) .step__num{background:var(--a1);color:var(--on-a1)}
.step:nth-child(2) .step__num{background:var(--a2);color:var(--on-a2)}
.step:nth-child(3) .step__num{background:var(--a3);color:var(--on-a3)}
.step:nth-child(4) .step__num{background:var(--a4);color:var(--on-a4)}
.step h3{font-size:18px;margin-bottom:10px}
.step p{font-size:15px;line-height:1.6;color:var(--mut);font-weight:600;margin-bottom:14px}
.step time{font-family:var(--fm);font-size:12px;font-weight:700;letter-spacing:.06em;
  border:2px solid var(--ink);background:var(--paper);padding:5px 10px;box-shadow:3px 3px 0 var(--shadow)}

/* отзывы */
.voices{display:grid;grid-template-columns:repeat(3,1fr);gap:30px}
.voice{border:var(--bw) solid var(--ink);background:var(--paper);padding:28px;
  box-shadow:8px 8px 0 var(--shadow);transition:transform .2s}
.voice:nth-child(1){transform:rotate(-2deg)}
.voice:nth-child(2){transform:rotate(1.6deg)}
.voice:nth-child(3){transform:rotate(-1.2deg)}
.voice:hover{transform:rotate(0) translate(-3px,-3px)}
.voice .q{font-family:var(--fd);font-size:34px;line-height:1;color:var(--a2)}
.voice p{font-size:16px;line-height:1.6;font-weight:600;margin:14px 0 22px}
.voice footer{font-family:var(--fm);font-size:12px;font-weight:700;letter-spacing:.06em;
  border-top:2px dashed var(--ink);padding-top:14px;color:var(--mut)}
.voice footer b{color:var(--ink)}

/* CTA */
.cta{position:relative;border:var(--bw) solid var(--ink);background:var(--a1);color:var(--on-a1);
  padding:clamp(36px,6vw,72px);box-shadow:14px 14px 0 var(--shadow);overflow:hidden}
.cta h2{font-weight:900;font-size:clamp(34px,6.4vw,80px);line-height:1;margin-bottom:18px}
.cta p{font-weight:800;font-size:17px;max-width:560px;margin-bottom:34px}
.cta__row{display:flex;gap:18px;flex-wrap:wrap;align-items:center}
.cta__mail{font-family:var(--fm);font-weight:700;font-size:clamp(14px,2vw,20px);letter-spacing:.04em;
  text-decoration:underline;text-underline-offset:6px}
.cta__badge{position:absolute;right:40px;bottom:40px;width:150px;height:150px}
.cta__badge svg{width:100%;height:100%;animation:spin 14s linear infinite}
.cta__badge text{font-family:var(--fm);font-weight:700;font-size:15.5px;letter-spacing:2.5px;fill:currentColor}
.cta__badge .arr{position:absolute;inset:0;display:grid;place-items:center;font-size:44px}
@keyframes spin{to{transform:rotate(360deg)}}

/* футер */
.ftr{background:var(--ink);color:var(--bg);margin-top:96px;transition:background .45s}
.ftr__giant{font-family:var(--fd);font-weight:900;font-size:clamp(64px,17vw,240px);line-height:.9;
  text-align:center;color:transparent;-webkit-text-stroke:3px var(--bg);padding:56px 16px 0;user-select:none}
.ftr__giant em{font-style:normal;-webkit-text-stroke:0;color:var(--a1)}
body[data-brand="volt"] .ftr__giant em{color:var(--a2)}
.ftr__grid{max-width:1280px;margin:0 auto;padding:48px 24px;display:grid;
  grid-template-columns:1.4fr 1fr 1fr 1fr;gap:32px}
.ftr h4{font-family:var(--fm);font-size:12px;letter-spacing:.12em;margin-bottom:16px;opacity:.7}
.ftr a{display:block;text-decoration:none;font-weight:600;font-size:15px;padding:5px 0;opacity:.9}
.ftr a:hover{opacity:1;text-decoration:underline;text-underline-offset:5px}
.ftr__about p{font-size:15px;line-height:1.6;opacity:.85;max-width:300px}
.ftr__bar{border-top:3px solid var(--bg)}
.ftr__bar .in{max-width:1280px;margin:0 auto;padding:18px 24px;display:flex;gap:20px;flex-wrap:wrap;
  justify-content:space-between;font-family:var(--fm);font-size:12px;font-weight:700;letter-spacing:.08em}

/* появление при скролле */
.rv{opacity:0;transform:translateY(34px)}
.rv.in{opacity:1;transform:translateY(0);transition:opacity .7s ease var(--d,0s),transform .7s cubic-bezier(.2,.8,.2,1) var(--d,0s)}

/* ================= АДАПТИВ ================= */
@media(max-width:1080px){
  .svc-grid{grid-template-columns:1fr 1fr}
  .voices{grid-template-columns:1fr 1fr}
  .voice:nth-child(3){display:none}
}
@media(max-width:960px){
  .nav{display:none}
  .burger{display:block}
  .hero__grid{grid-template-columns:1fr;gap:64px}
  .hero__frame{max-width:560px}
  .manifest__cards{grid-template-columns:1fr}
  .steps{grid-template-columns:1fr 1fr}
  .steps::before{display:none}
  .stats__in{grid-template-columns:1fr 1fr;gap:40px 0}
  .stat:nth-child(3){border-left:none;padding-left:0}
  .ftr__grid{grid-template-columns:1fr 1fr}
  .cta__badge{display:none}
}
@media(max-width:640px){
  .sec{padding:72px 0}
  .svc-grid,.cases,.voices,.steps{grid-template-columns:1fr}
  .voice:nth-child(3){display:block}
  .sw{padding:7px 8px;font-size:11px;gap:5px}
  .sw i{width:11px;height:11px}
  .hdr__in{gap:12px;padding:12px 16px}
  .hero{padding:48px 0 72px}
  .hero__st2{right:6px}
  .sec__hint{text-align:left}
  .stats__in{grid-template-columns:1fr;gap:36px}
  .stat{border-left:none;padding:0}
  .ftr__grid{grid-template-columns:1fr}
}
@media(prefers-reduced-motion:reduce){
  *,*::before,*::after{animation-duration:.01ms!important;transition-duration:.01ms!important}
  .rv{opacity:1;transform:none}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const BRUTAL_LANDING_HTML_BODY = `
<div class="noise" aria-hidden="true"></div>
<div class="cursor" id="cursor" aria-hidden="true"></div>

<!-- ШАПКА -->
<header class="hdr">
  <div class="hdr__in">
    <a class="logo" href="#top">BST<sup>®</sup></a>
    <nav class="nav" aria-label="Основная навигация">
      <a href="#manifest">Манифест</a>
      <a href="#services">Услуги</a>
      <a href="#cases">Кейсы</a>
      <a href="#process">Процесс</a>
      <a href="#contact">Контакт</a>
    </nav>
    <div class="hdr__right">
      <div class="switch" id="switch" role="tablist" aria-label="Вариант бренда: 4 в одном">
        <button class="sw is-on" data-b="acid" title="BST ACID — стритвир"><i></i>01</button>
        <button class="sw" data-b="volt" title="BST VOLT — финтех"><i></i>02</button>
        <button class="sw" data-b="pop" title="BST POP — косметика"><i></i>03</button>
        <button class="sw" data-b="noir" title="BST NOIR — аудио-тех"><i></i>04</button>
      </div>
      <button class="burger" id="burger" aria-label="Открыть меню"><span></span><span></span></button>
    </div>
  </div>
</header>

<!-- МОБИЛЬНОЕ МЕНЮ -->
<div class="menu" id="menu">
  <a class="big" href="#manifest">Манифест <em>✦</em></a>
  <a class="big" href="#services">Услуги <em>✦</em></a>
  <a class="big" href="#cases">Кейсы <em>✦</em></a>
  <a class="big" href="#process">Процесс <em>✦</em></a>
  <a class="big" href="#contact">Контакт <em>✦</em></a>
  <div class="menu__foot">
    <span>HELLO@BST.STUDIO</span><span>+7 900 000-00-00</span><span>КЛАВИШИ 1–4 — СМЕНА БРЕНДА</span>
  </div>
</div>

<main id="top">

  <!-- ХИРО -->
  <section class="hero">
    <span class="fl fl--1" aria-hidden="true"><svg width="70" height="70" viewBox="0 0 100 100"><path d="M50 4 61 36 96 36 68 57 78 92 50 71 22 92 32 57 4 36 39 36Z" fill="var(--a2)" stroke="var(--ink)" stroke-width="6"/></svg></span>
    <span class="fl fl--2" aria-hidden="true"><svg width="64" height="64" viewBox="0 0 100 100"><circle cx="50" cy="50" r="42" fill="var(--a3)" stroke="var(--ink)" stroke-width="6"/></svg></span>
    <span class="fl fl--3" aria-hidden="true"><svg width="60" height="60" viewBox="0 0 100 100"><rect x="12" y="12" width="76" height="76" fill="var(--a1)" stroke="var(--ink)" stroke-width="6" transform="rotate(12 50 50)"/></svg></span>

    <div class="wrap hero__grid">
      <div>
        <p id="htKicker" class="eyebrow rv in">[ <b data-brand-name>BST ACID</b> · <span data-brand-field>СТРИТВИР</span> · СИСТЕМА 4-В-1 ]</p>
        <h1 class="hero__title">
          <span id="htWord" class="ht-line">ДЕЛАЕМ</span>
          <span class="ht-line"><span class="ht-mark">ГРОМКИЕ</span></span>
          <span class="ht-line ht-outline">БРЕНДЫ<em class="ht-reg">®</em></span>
        </h1>
        <p id="htTag" class="hero__text rv in">Мы — студия, которая собирает айдентику, сайты и продукты с характером. <b>Одна система — четыре характера.</b> Переключайте варианты наверху: палитра, графика и вайб меняются на лету.</p>
        <div class="hero__cta rv" style="--d:.1s">
          <a class="btn btn--a1" href="#cases">Смотреть кейсы ↓</a>
          <a class="btn" href="#contact">Обсудить проект</a>
        </div>
        <div class="hero__tags rv" style="--d:.2s">
          <span>БРЕНДИНГ</span><span>ВЕБ</span><span>УПАКОВКА</span><span>МОУШН</span><span>СТРАТЕГИЯ</span>
        </div>
      </div>

      <div class="rv" style="--d:.15s">
        <figure class="hero__frame">
          <span id="st1" class="sticker hero__st1">★ NEW!</span>
          <span id="st2" class="sticker hero__st2">100% ГРОМКО</span>
          <img id="heroImg" data-img="heroImg" src="https://image.qwenlm.ai/public_source/fd128d39-a2d2-4d36-8bfd-d3c542ce05e6/1b795f1a9-c53e-4ccf-a3bb-35159ac6b134.png" alt="Визуал активного бренда BST">
          <figcaption class="hero__cap"><span>FIG. <span data-brand-num>01</span> — <span data-brand-name>BST ACID</span></span><span>ВЕРСИЯ <span data-brand-num>01</span>/04</span></figcaption>
        </figure>
      </div>
    </div>
    <a class="scroll" href="#manifest">СКРОЛЛ ↓</a>
  </section>

  <!-- ТИКЕР -->
  <div class="ticker" aria-hidden="true">
    <div class="ticker__track">
      <span>БРЕНДИНГ ✦ АЙДЕНТИКА ✦ ВЕБ-ДИЗАЙН ✦ РАЗРАБОТКА ✦ УПАКОВКА ✦ МОУШН ✦ СТРАТЕГИЯ ✦ BST® ✦ БРЕНДИНГ ✦ АЙДЕНТИКА ✦ ВЕБ-ДИЗАЙН ✦ РАЗРАБОТКА ✦ УПАКОВКА ✦ МОУШН ✦ СТРАТЕГИЯ ✦ BST® ✦&nbsp;</span>
      <span>БРЕНДИНГ ✦ АЙДЕНТИКА ✦ ВЕБ-ДИЗАЙН ✦ РАЗРАБОТКА ✦ УПАКОВКА ✦ МОУШН ✦ СТРАТЕГИЯ ✦ BST® ✦ БРЕНДИНГ ✦ АЙДЕНТИКА ✦ ВЕБ-ДИЗАЙН ✦ РАЗРАБОТКА ✦ УПАКОВКА ✦ МОУШН ✦ СТРАТЕГИЯ ✦ BST® ✦&nbsp;</span>
    </div>
  </div>

  <!-- МАНИФЕСТ -->
  <section class="sec" id="manifest">
    <div class="wrap">
      <div class="sec__head rv">
        <div><span class="sec__label">01 / МАНИФЕСТ</span><h2>ТИШЕ — <span class="mark">ЭТО СКУЧНО</span></h2></div>
      </div>
      <p class="manifest rv">Мы верим, что дизайн должен <span class="mark mark--a2">кричать</span>, а не шептать. Сетка наружу, тени без блюра, шрифты на максимум — <span class="mark mark--a3">честный брутализм</span> продаёт лучше глянца.</p>
      <div class="manifest__cards">
        <article class="mcard rv"><div class="ico">✖</div><h3 data-t="man1Title">Без шаблонов</h3><p data-t="man1Desc">Каждый проект собираем с нуля: исследование, концепция, система. Никаких «готовых решений» из стоков.</p></article>
        <article class="mcard rv" style="--d:.1s"><div class="ico">₽</div><h3 data-t="man2Title">Фикс-прайс</h3><p data-t="man2Desc">Смета закреплена до старта работ. Никаких «ой, это будет стоить ещё столько же» в конце месяца.</p></article>
        <article class="mcard rv" style="--d:.2s"><div class="ico">⚡</div><h3 data-t="man3Title">Срок от 14 дней</h3><p data-t="man3Desc">Первая боевая версия бренда — через две недели. Дальше — итерации, тесты, запуск.</p></article>
      </div>
    </div>
  </section>

  <!-- УСЛУГИ -->
  <section class="sec" id="services" style="padding-top:0">
    <div class="wrap">
      <div class="sec__head rv">
        <div><span class="sec__label">02 / УСЛУГИ</span><h2>ПОЛНЫЙ ЦИКЛ <span class="mark">ГРОМКОСТИ</span></h2></div>
        <p class="sec__hint">От нейминга до запуска. Наведите на карточку — увидите акцент бренда.</p>
      </div>
      <div class="svc-grid">
        <article class="svc rv"><div class="svc__top"><span class="svc__num">S.01</span><span class="svc__arr">→</span></div><h3 data-t="svc1Title">Брендинг</h3><p data-t="svc1Desc">Нейминг, логотип, айдентика, брендбук. Система, которая работает везде — от аватарки до билборда.</p></article>
        <article class="svc rv" style="--d:.05s"><div class="svc__top"><span class="svc__num">S.02</span><span class="svc__arr">→</span></div><h3 data-t="svc2Title">Веб-дизайн</h3><p data-t="svc2Desc">Лендинги, e-commerce, промо-сайты. Дизайн, который невозможно пролистать и невозможно забыть.</p></article>
        <article class="svc rv" style="--d:.1s"><div class="svc__top"><span class="svc__num">S.03</span><span class="svc__arr">→</span></div><h3 data-t="svc3Title">Разработка</h3><p data-t="svc3Desc">Быстрые сайты и магазины, CMS, интеграции. Код чище, чем совесть после дедлайна.</p></article>
        <article class="svc rv"><div class="svc__top"><span class="svc__num">S.04</span><span class="svc__arr">→</span></div><h3 data-t="svc4Title">Упаковка</h3><p data-t="svc4Desc">Пачки, этикетки, мерч. Упаковка, которую фотографируют до того, как открывают.</p></article>
        <article class="svc rv" style="--d:.05s"><div class="svc__top"><span class="svc__num">S.05</span><span class="svc__arr">→</span></div><h3 data-t="svc5Title">Моушн</h3><p data-t="svc5Desc">Рекламные ролики, анимация логотипа, UI-анимации. Движение — это половина характера.</p></article>
        <article class="svc rv" style="--d:.1s"><div class="svc__top"><span class="svc__num">S.06</span><span class="svc__arr">→</span></div><h3 data-t="svc6Title">Стратегия</h3><p data-t="svc6Desc">Исследования, позиционирование, tone of voice. Сначала смысл — потом громкость.</p></article>
      </div>
    </div>
  </section>

  <!-- СТАТИСТИКА -->
  <section class="stats">
    <div class="stats__in">
      <div class="stat rv"><b><span data-to="150">0</span><i>+</i></b><span data-t="stat1L">ПРОЕКТОВ ЗАПУЩЕНО</span></div>
      <div class="stat rv" style="--d:.08s"><b><span data-to="27">0</span><i>×</i></b><span data-t="stat2L">НАГРАД И ШОРТ-ЛИСТОВ</span></div>
      <div class="stat rv" style="--d:.16s"><b><span data-to="9">0</span><i>лет</i></b><span data-t="stat3L">ДЕЛАЕМ ШУМ</span></div>
      <div class="stat rv" style="--d:.24s"><b><span data-to="98">0</span><i>%</i></b><span data-t="stat4L">КЛИЕНТОВ ВОЗВРАЩАЮТСЯ</span></div>
    </div>
  </section>

  <!-- КЕЙСЫ -->
  <section class="sec" id="cases">
    <div class="wrap">
      <div class="sec__head rv">
        <div><span class="sec__label">03 / КЕЙСЫ</span><h2>4 ХАРАКТЕРА — <span class="mark">1 СИСТЕМА</span></h2></div>
        <p class="sec__hint">Переключайте бренд кнопками 01–04 (или клавишами 1–4) — активный кейс получит бейдж.</p>
      </div>
      <div class="cases">
        <article class="case rv" data-case="acid">
          <div class="case__img"><span class="case__badge">★ АКТИВНЫЙ БРЕНД</span><img data-img="case1img" src="https://image.qwenlm.ai/public_source/fd128d39-a2d2-4d36-8bfd-d3c542ce05e6/120c44a04-e978-4f1a-9540-a228b0496710.png" alt="BST ACID — стритвир" loading="lazy"></div>
          <div class="case__body">
            <div class="case__meta"><b>К.01</b><span>СТРИТВИР</span><span>2025</span></div>
            <h3 data-t="case1Title">BST ACID — дроп «Кислота»</h3>
            <p data-t="case1Desc">Айдентика, упаковка и поп-ап стор для уличного бренда. Тираж дропа разошёлся за 72 часа.</p>
            <div class="case__tags"><span>АЙДЕНТИКА</span><span>УПАКОВКА</span><span>РИТЕЙЛ</span></div>
          </div>
        </article>
        <article class="case rv" data-case="volt" style="--d:.08s">
          <div class="case__img"><span class="case__badge">★ АКТИВНЫЙ БРЕНД</span><img data-img="case2img" src="https://image.qwenlm.ai/public_source/fd128d39-a2d2-4d36-8bfd-d3c542ce05e6/19c03da51-f71f-4fbc-9f4a-440d71324802.png" alt="BST VOLT — финтех" loading="lazy"></div>
          <div class="case__body">
            <div class="case__meta"><b>К.02</b><span>ФИНТЕХ</span><span>2024</span></div>
            <h3 data-t="case2Title">BST VOLT — платформа «Заряд»</h3>
            <p data-t="case2Desc">Продуктовый дизайн, веб и дизайн-система. Конверсия в заявку выросла на 212% после редизайна.</p>
            <div class="case__tags"><span>ПРОДУКТ</span><span>ВЕБ</span><span>СИСТЕМА</span></div>
          </div>
        </article>
        <article class="case rv" data-case="pop">
          <div class="case__img"><span class="case__badge">★ АКТИВНЫЙ БРЕНД</span><img data-img="case3img" src="https://image.qwenlm.ai/public_source/fd128d39-a2d2-4d36-8bfd-d3c542ce05e6/10d7ef6c1-ad39-4a3a-9a86-8b1631147e7d.png" alt="BST POP — косметика" loading="lazy"></div>
          <div class="case__body">
            <div class="case__meta"><b>К.03</b><span>КОСМЕТИКА</span><span>2024</span></div>
            <h3 data-t="case3Title">BST POP — линейка «Сироп»</h3>
            <p data-t="case3Desc">Брендинг, e-com и моушн для бьюти-линейки. 40 000 сохранёнок в соцсетях за первый месяц.</p>
            <div class="case__tags"><span>БРЕНДИНГ</span><span>E-COM</span><span>МОУШН</span></div>
          </div>
        </article>
        <article class="case rv" data-case="noir" style="--d:.08s">
          <div class="case__img"><span class="case__badge">★ АКТИВНЫЙ БРЕНД</span><img data-img="case4img" src="https://image.qwenlm.ai/public_source/fd128d39-a2d2-4d36-8bfd-d3c542ce05e6/17c45132b-e99d-4304-a61f-72e0824bc706.png" alt="BST NOIR — аудио-тех" loading="lazy"></div>
          <div class="case__body">
            <div class="case__meta"><b>К.04</b><span>АУДИО-ТЕХ</span><span>2023</span></div>
            <h3 data-t="case4Title">BST NOIR — саунд «Тишина»</h3>
            <p data-t="case4Desc">Айдентика и сайт запуска акустики премиум-класса. Предзаказ закрыли за первую неделю.</p>
            <div class="case__tags"><span>АЙДЕНТИКА</span><span>САЙТ</span><span>ЗАПУСК</span></div>
          </div>
        </article>
      </div>
    </div>
  </section>

  <!-- ПРОЦЕСС -->
  <section class="sec" id="process" style="padding-top:0">
    <div class="wrap">
      <div class="sec__head rv">
        <div><span class="sec__label">04 / ПРОЦЕСС</span><h2>БЕЗ ХАОСА — <span class="mark">ПО СИСТЕМЕ</span></h2></div>
      </div>
      <div class="steps">
        <div class="step rv"><div class="step__num">01</div><h3 data-t="step1Title">Исследование</h3><p data-t="step1Desc">Погружаемся в рынок, аудиторию и конкурентов. Находим, где можно включить громкость.</p><time>3–5 ДНЕЙ</time></div>
        <div class="step rv" style="--d:.08s"><div class="step__num">02</div><h3 data-t="step2Title">Концепция</h3><p data-t="step2Desc">2–3 направления системы: палитра, типографика, графика, тон. Защищаем смелое.</p><time>1–2 НЕДЕЛИ</time></div>
        <div class="step rv" style="--d:.16s"><div class="step__num">03</div><h3 data-t="step3Title">Продакшн</h3><p data-t="step3Desc">Раскатываем систему на все носители: сайт, упаковка, соцсети, мерч, реклама.</p><time>2–6 НЕДЕЛЬ</time></div>
        <div class="step rv" style="--d:.24s"><div class="step__num">04</div><h3 data-t="step4Title">Запуск</h3><p data-t="step4Desc">Выкатываем, измеряем, усиливаем. Остаёмся рядом и поддерживаем шум.</p><time>∞ ПОДДЕРЖКА</time></div>
      </div>
    </div>
  </section>

  <!-- ОТЗЫВЫ -->
  <section class="sec" id="voices" style="padding-top:0">
    <div class="wrap">
      <div class="sec__head rv">
        <div><span class="sec__label">05 / ОТЗЫВЫ</span><h2>КЛИЕНТЫ <span class="mark mark--a2">КРИЧАТ</span></h2></div>
      </div>
      <div class="voices">
        <article class="voice rv"><div class="q">"</div><p data-t="rev1Quote">Ребята сделали бренд, который бесит конкурентов и радует инвесторов. KPI выросли ещё до запуска.</p><footer><b data-t="rev1Author">М. СТОЛЕТОВ</b> — CEO, BST VOLT</footer></article>
        <article class="voice rv" style="--d:.1s"><div class="q">"</div><p data-t="rev2Quote">Сроки, смета и шок-контент — всё по фикс-прайсу и в срок. В нашем рынке это редкость.</p><footer><b data-t="rev2Author">А. КИМ</b> — CMO, BST POP</footer></article>
        <article class="voice rv" style="--d:.2s"><div class="q">"</div><p data-t="rev3Quote">Наш логотип заметили с другой стороны улицы. Я считаю, это победа.</p><footer><b data-t="rev3Author">Д. ГРОМ</b> — основатель, BST ACID</footer></article>
      </div>
    </div>
  </section>

  <!-- CTA -->
  <section class="sec" id="contact" style="padding-top:0">
    <div class="wrap">
      <div class="cta rv">
        <h2 data-t="ctaTitle">ГОТОВЫ<br>ЗАШУМЕТЬ?</h2>
        <p data-t="ctaDesc">Расскажите о задаче — вернёмся с концепцией, сметой и планом запуска. Бесплатно и громко.</p>
        <div class="cta__row">
          <a class="btn btn--inv" href="mailto:hello@bst.studio">Обсудить проект →</a>
          <a class="cta__mail" href="mailto:hello@bst.studio">HELLO@BST.STUDIO</a>
        </div>
        <div class="cta__badge" aria-hidden="true">
          <svg viewBox="0 0 200 200">
            <defs><path id="cir" d="M100,100 m-80,0 a80,80 0 1,1 160,0 a80,80 0 1,1 -160,0"/></defs>
            <text><textPath href="#cir">ОБСУДИТЬ ПРОЕКТ ✦ ОТВЕЧАЕМ ЗА 24 ЧАСА ✦ BST® ✦</textPath></text>
          </svg>
          <span class="arr">→</span>
        </div>
      </div>
    </div>
  </section>
</main>

<!-- ФУТЕР -->
<footer class="ftr">
  <div class="ftr__giant" aria-hidden="true">BST<em>®</em></div>
  <div class="ftr__grid">
    <div class="ftr__about">
      <h4>СТУДИЯ</h4>
      <p>BST® — независимая студия нео-брутализма. Одна система, четыре характера бренда: ACID, VOLT, POP и NOIR.</p>
    </div>
    <div>
      <h4>МЕНЮ</h4>
      <a href="#manifest">Манифест</a><a href="#services">Услуги</a><a href="#cases">Кейсы</a><a href="#process">Процесс</a>
    </div>
    <div>
      <h4>СОЦСЕТИ</h4>
      <a href="#">Telegram ↗</a><a href="#">Behance ↗</a><a href="#">Dribbble ↗</a><a href="#">VK ↗</a>
    </div>
    <div>
      <h4>КОНТАКТЫ</h4>
      <a href="mailto:hello@bst.studio">hello@bst.studio</a>
      <a href="tel:+79000000000">+7 900 000-00-00</a>
      <a href="#">Москва, ЦСИ «Флакон»</a>
    </div>
  </div>
  <div class="ftr__bar">
    <div class="in">
      <span>© 2025 BST® STUDIO — СДЕЛАНО ГРОМКО</span>
      <span id="clock">00:00:00</span>
      <a href="#top">НАВЕРХ ↑</a>
    </div>
  </div>
</footer>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function brutalLandingEngineJs(brand: string, mode: 'canvas' | 'preview'): string {
  return `
(function(){
  "use strict";
  var $ = function(s,c){ return (c||document).querySelector(s); };
  var $$ = function(s,c){ return Array.prototype.slice.call((c||document).querySelectorAll(s)); };

  /* ---------- ДАННЫЕ 4 БРЕНДОВ ---------- */
  var BRANDS = {
    acid:{name:"BST ACID", field:"СТРИТВИР", num:"01",
      hero:"https://image.qwenlm.ai/public_source/fd128d39-a2d2-4d36-8bfd-d3c542ce05e6/1b795f1a9-c53e-4ccf-a3bb-35159ac6b134.png"},
    volt:{name:"BST VOLT", field:"ФИНТЕХ", num:"02",
      hero:"https://image.qwenlm.ai/public_source/fd128d39-a2d2-4d36-8bfd-d3c542ce05e6/1cf4ad29c-3039-4147-ad3e-1b5af3b308ec.png"},
    pop:{name:"BST POP", field:"КОСМЕТИКА", num:"03",
      hero:"https://image.qwenlm.ai/public_source/fd128d39-a2d2-4d36-8bfd-d3c542ce05e6/1b2f5879d-c125-451d-87ca-54a2cd9d16d8.png"},
    noir:{name:"BST NOIR", field:"АУДИО-ТЕХ", num:"04",
      hero:"https://image.qwenlm.ai/public_source/fd128d39-a2d2-4d36-8bfd-d3c542ce05e6/1a09cd7a3-8c3a-4d76-a814-53ae75729770.png"}
  };
  var ORDER = ["acid","volt","pop","noir"];

  ORDER.forEach(function(k){ var im = new Image(); im.src = BRANDS[k].hero; });

  var heroImg = $("#heroImg");

  function setBrand(id){
    if(!BRANDS[id]) return;
    var b = BRANDS[id];
    document.body.dataset.brand = id;

    $$("[data-brand-name]").forEach(function(el){ el.textContent = b.name; });
    $$("[data-brand-field]").forEach(function(el){ el.textContent = b.field; });
    $$("[data-brand-num]").forEach(function(el){ el.textContent = b.num; });

    if(heroImg.getAttribute("src") !== b.hero){
      heroImg.classList.remove("pop");
      void heroImg.offsetWidth;
      heroImg.src = b.hero;
      heroImg.classList.add("pop");
    }
    $$(".sw").forEach(function(btn){ btn.classList.toggle("is-on", btn.dataset.b === id); });
    $$(".case").forEach(function(card){ card.classList.toggle("is-active", card.dataset.case === id); });
    document.title = b.name + "® — студия громких брендов";
  }

  $$(".sw").forEach(function(btn){
    btn.addEventListener("click", function(){ setBrand(btn.dataset.b); });
  });
  document.addEventListener("keydown", function(e){
    var i = ["1","2","3","4"].indexOf(e.key);
    if(i > -1) setBrand(ORDER[i]);
  });
  setBrand("${brand}");

  /* ---------- МОБИЛЬНОЕ МЕНЮ ---------- */
  var burger = $("#burger"), menu = $("#menu");
  burger.addEventListener("click", function(){
    var open = menu.classList.toggle("open");
    burger.classList.toggle("open", open);
    document.body.style.overflow = open ? "hidden" : "";
  });
  $$("a", menu).forEach(function(a){
    a.addEventListener("click", function(){
      menu.classList.remove("open");
      burger.classList.remove("open");
      document.body.style.overflow = "";
    });
  });

  /* ---------- ПОЯВЛЕНИЕ ПРИ СКРОЛЛЕ ---------- */
  var io = new IntersectionObserver(function(entries){
    entries.forEach(function(en){
      if(en.isIntersecting){ en.target.classList.add("in"); io.unobserve(en.target); }
    });
  }, {threshold:.15});
  $$(".rv").forEach(function(el){ io.observe(el); });

  /* ---------- СЧЁТЧИКИ ---------- */
  var cio = new IntersectionObserver(function(entries){
    entries.forEach(function(en){
      if(!en.isIntersecting) return;
      var el = en.target, to = +el.dataset.to, t0 = null;
      cio.unobserve(el);
      function stepFn(ts){
        if(!t0) t0 = ts;
        var p = Math.min((ts - t0)/1400, 1);
        el.textContent = Math.round(to * (1 - Math.pow(1-p, 3)));
        if(p < 1) requestAnimationFrame(stepFn);
      }
      requestAnimationFrame(stepFn);
    });
  }, {threshold:.6});
  $$("[data-to]").forEach(function(el){ cio.observe(el); });

  /* ---------- КУРСОР ---------- */
  var cursor = $("#cursor");
  if(window.matchMedia("(hover:hover) and (pointer:fine)").matches){
    var cx = -100, cy = -100, tx = -100, ty = -100;
    document.addEventListener("mousemove", function(e){ tx = e.clientX; ty = e.clientY; });
    (function loop(){
      cx += (tx - cx) * .2; cy += (ty - cy) * .2;
      cursor.style.left = cx + "px"; cursor.style.top = cy + "px";
      requestAnimationFrame(loop);
    })();
    $$("a, button, .svc, .case").forEach(function(el){
      el.addEventListener("mouseenter", function(){ cursor.classList.add("big"); });
      el.addEventListener("mouseleave", function(){ cursor.classList.remove("big"); });
    });
  }

  /* ---------- ЧАСЫ В ФУТЕРЕ ---------- */
  var clock = $("#clock");
  function tickClock(){ clock.textContent = new Date().toLocaleTimeString("ru-RU"); }
  tickClock(); setInterval(tickClock, 1000);
})();
${mode === 'canvas' ? `(function(){
  document.querySelectorAll('.reveal,.rv,[data-rv],.in-view').forEach(function(el){el.classList.add('in','is-active');});
  document.querySelectorAll('.line-inner,.lm').forEach(function(el){el.style.transform='none';el.style.opacity='1';});
  document.body.classList.add('loaded','is-ready');
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
  document.querySelectorAll('form').forEach(function(f){f.addEventListener('submit',function(e){e.preventDefault();});});
})();` : ''}
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getBrutalLandingPageHtml(data: BrutalLandingPageData, mode: 'canvas' | 'preview'): string {
  const brand = data.brand ?? 'acid'
  return `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>BST® — студия громких брендов</title>
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded@latest/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded@latest/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded@latest/900.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope@latest/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope@latest/600.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope@latest/800.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/jetbrains-mono@latest/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/jetbrains-mono@latest/700.css">
<style>${BRUTAL_LANDING_CSS}</style>
</head>
<body data-brand="${brand}">
${BRUTAL_LANDING_HTML_BODY}
<script>${brutalLandingEngineJs(brand, mode)}</script>
</body>
</html>`
}
